import React, { useEffect, useState, useRef, useCallback } from 'react'
import zoomSdk, { OnParticipantChangeEvent } from '@zoom/appssdk'
import { MeetingParticipant } from '../../modules/meeting/model'
import useRegisterParticipants from '../../modules/meeting/hooks/useRegisterParticipants'
import ParticipantsList from './ParticipantsList'
import { Button } from '../../onsaui'
import {
  EmailRequestStatus,
  EmailRequestStatusToLabelMap,
} from '../../modules/meeting/model/meetingHelpers'

const ParticipantMeetingInfo: React.FC<{
  meetingId: string
}> = ({ meetingId }) => {
  const { registerParticipants } = useRegisterParticipants()
  const [participants, setParticipants] = useState<MeetingParticipant[]>([])
  const participantsRef = useRef<MeetingParticipant[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const emailFetchAttempted = useRef(false)
  const [emailRequestStatus, setEmailRequestStatus] = useState<EmailRequestStatus>(
    EmailRequestStatus.NotRequested,
  )

  const handleEmailResponse = useCallback(
    async (event: any) => {
      console.log('📨 Raw email response event received', event)

      if (!event?.participantUUID || !event?.participantEmail) {
        console.log('❌ Invalid email response event', { event })
        return
      }

      const currentParticipants = participantsRef.current

      const existingParticipant = currentParticipants.find(
        (p) => p.participantUUID === event.participantUUID,
      )

      if (!existingParticipant?.participantUUID || !existingParticipant?.screenName) {
        console.log('❌ Invalid existing participant', existingParticipant)
        return
      }

      const meeting = await registerParticipants(meetingId, {
        participants: [
          {
            participantUUID: event.participantUUID,
            screenName: existingParticipant?.screenName || 'Unknown',
            participantEmail: event.participantEmail,
            timestamp: event.timestamp,
            leftMeeting: false,
          },
        ],
      })
      if (meeting) {
        setParticipants(meeting.participants || [])
      }
    },
    [meetingId, participants, registerParticipants],
  )

  const handleParticipantChange = useCallback(
    async (event: OnParticipantChangeEvent) => {
      console.log('👤 Participant change event received', event)

      let updatedParticipants = []

      for (const participant of event.participants) {
        if (!participant.participantUUID) {
          console.log('❌ Invalid participant change event', { event })
          return
        }

        console.log('👤 Participant change for', participant.participantUUID)

        updatedParticipants.push({
          participantUUID: participant.participantUUID,
          screenName: participant.screenName,
          role: participant.role,
          timestamp: event.timestamp,
          leftMeeting: participant.status === 'leave',
        })
      }

      const meeting = await registerParticipants(meetingId, {
        participants: updatedParticipants,
      })

      if (meeting) {
        setParticipants(meeting.participants || [])
      }
    },
    [registerParticipants, meetingId],
  )

  const requestEmails = async () => {
    if (emailFetchAttempted.current) {
      console.log('📧 Email fetch already attempted, skipping')
      return
    }

    try {
      console.log('📧 Requesting participant emails')

      setEmailRequestStatus(EmailRequestStatus.Requesting)
      emailFetchAttempted.current = true
      const response = await zoomSdk.getMeetingParticipantsEmail({
        reasonForAsking:
          'Onsa.ai will use your email to send the meeting recap and transcript to your inbox.',
      })

      if (response?.message === 'Success') {
        setEmailRequestStatus(EmailRequestStatus.Requested)
        console.log('📧 Email request successful')
      } else {
        emailFetchAttempted.current = false
        setEmailRequestStatus(EmailRequestStatus.Error)
        console.log('📧 Email request failed', response)
      }
    } catch (error: any) {
      console.log('❌ Email request error', {
        error: error.message,
        stack: error.stack,
      })

      if (error.message.includes('permission') || error.message.includes('auth')) {
        emailFetchAttempted.current = false
        setEmailRequestStatus(EmailRequestStatus.PermissionDenied)
        console.log('🔒 Email permission denied')
      } else {
        setEmailRequestStatus(EmailRequestStatus.Error)
      }
    }
  }

  const resendEmailRequest = async () => {
    emailFetchAttempted.current = false
    setEmailRequestStatus(EmailRequestStatus.NotRequested)

    requestEmails()
  }

  useEffect(() => {
    console.log('👂 Setting up response listeners')

    try {
      if (typeof zoomSdk.onParticipantEmail !== 'function') {
        throw new Error('onParticipantEmail is not a function in the SDK')
      }

      if (typeof zoomSdk.onParticipantChange !== 'function') {
        throw new Error('onParticipantChange is not a function in the SDK')
      }
      zoomSdk.onParticipantEmail((event: any) => {
        handleEmailResponse(event)
      })
      zoomSdk.onParticipantChange((event: any) => {
        handleParticipantChange(event)
      })
    } catch (error) {
      console.log('❌ Error setting up email listener:', error)
    }
  }, [])

  useEffect(() => {
    if (participants.length > 0) {
      requestEmails()
    }
  }, [participants.length])

  useEffect(() => {
    const fetchAndRefresh = async () => {
      try {
        const participantsInfo = await zoomSdk.getMeetingParticipants()
        const participantsArray = participantsInfo?.participants || []

        const meeting = await registerParticipants(meetingId, {
          participants: participantsArray.map((p) => ({
            participantUUID: p.participantUUID,
            screenName: p.screenName,
            role: p.role,
            timestamp: new Date().getTime(),
            leftMeeting: false,
          })),
        })

        if (meeting && meeting.participants) {
          setParticipants(meeting.participants!)
        }

        setError(null)
      } catch (error: any) {
        console.log('❌ Error fetching participants', { error: error.message })
        setError(error.message || 'Error fetching participants')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAndRefresh()
    // const interval = setInterval(fetchAndRefresh, 20000)
    // return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    console.log('👥 Participants changed', participants)
    participantsRef.current = participants
  }, [participants])

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto rounded-lg bg-white pb-4">
      <ParticipantsList participants={participants} isLoading={isLoading} error={error} />
      <div className="flex flex-row items-center justify-between px-2 text-sm text-gray-600">
        <p>
          Email Request Status:{' '}
          <span className="font-semibold">{EmailRequestStatusToLabelMap[emailRequestStatus]}</span>
        </p>
        {}
        <Button
          onClick={resendEmailRequest}
          disabled={emailRequestStatus === EmailRequestStatus.Requesting}
          size="small"
          variant="outlined"
        >
          Request Emails
        </Button>
      </div>
    </div>
  )
}

export default ParticipantMeetingInfo
